import React, { useState, useCallback, useMemo, useRef, useContext } from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { Formik, FormikProps } from 'formik'
import { useFetch } from 'hooks/useFetch'
import {
  ARBITRATION_MAX_DOCUMENTS,
  ARBITRATION_MIN_PHYSICAL_DAMAGE_DOCUMENTS,
  ARBITRATION_REASON_MAX_LENGTH,
  ArbitrationIssueType,
  ArbitrationState,
  IArbitrationDetail,
  IArbitrationDocumentSubmit,
  IArbitrationFormValues,
  IArbitrationMetadata,
  IArbitrationResult,
  arbitrationFormSchema,
  initialArbitrationValues
} from 'types/arbitrationTypes'
import {
  addArbitrationDocuments,
  getArbitrationInfo,
  getArbitrationMetadata,
  submitArbitration
} from 'apis/arbitrationApi'
import {
  StyledArbitrationFormBody,
  StyledArbitrationFormLoading,
  StyledArbitrationModal,
  StyledFormSubmitOverlay
} from './ArbitrationForm.styled'
import { ArbitrationRequestSection } from './sections/ArbitrationRequestSection'
import { getBase64Content, toBase64 } from 'utils/fileUtils'
import { SaleInfoSection } from './sections/SaleInfoSection'
import { ContactInfoSection } from './sections/ContactInfoSection'
import { ConfirmSubmitModal } from './sections/ConfirmSubmitModal'
import { ToastContext } from 'contexts/ToastContext'

interface IProps {
  vehicleInstanceID: number
  buyerVehiclePurchaseID: number
  vin: string
  arbitrationState: ArbitrationState
  isUpdateAttachment: boolean
  metadata?: IArbitrationMetadata | null
  onClose: () => void
  onSuccess: (result: IArbitrationResult, shouldRefreshList?: boolean) => void
}

const EMPTY_UPLOADER_TEXT = 'Drag or click to add attachments'
const ATTACHMENTS_REQUIRED_MESSAGE = 'Attachments are required'
const FILE_SIZE_EXCEEDED_MESSAGE = 'File size exceeds the maximum allowed limit.'
const UNSUPPORTED_FILE_TYPE_MESSAGE = 'File type is not supported. Please check accepted file types.'
const PHYSICAL_DAMAGE_ISSUE_HINT = 'Documents required: Bill of Lading, Estimate, Photos, Transportation Invoice.'
const NON_PHYSICAL_DAMAGE_DOCUMENT_HINT =
  'If title issue, please include scan of front/back of title and any supporting documents.'
const MILEAGE_BELOW_WARNING =
  'Please validate the mileage. Mileage entered is less than mileage at time of vehicle purchase.'
const MILEAGE_EXCEED_WARNING = 'Mileage exceeds allowable transportation mileage, arbitration may not be considered.'
const PHYSICAL_DAMAGE_SUBMIT_WARNING =
  'Arbitration Claims submitted without the listed supporting documents will remain invalid and will not be reviewed until all required documents are provided: Bill of Lading, Estimate, Photos, Transportation Invoice.'

const getMaximumAttachmentsMessage = (max: number) => `Maximum ${max} attachments allowed`
const getMinimumAttachmentsMessage = (min: number) => `Minimum ${min} attachments required`

const getAcceptedFileExtensions = (attachmentFormats?: string[]) =>
  (attachmentFormats?.length
    ? attachmentFormats
    : ['png', 'jpg', 'jpeg', 'jpe', 'jfif', 'mov', 'msg', 'pdf', 'xlsx', 'docx', 'mp4']
  ).map((format) => format.replace(/^\./, '').toLowerCase())

const parseMileageInput = (value: string): number | null => {
  const normalizedValue = value.replace(/,/g, '').trim()
  if (normalizedValue === '') {
    return null
  }

  const mileage = Number(normalizedValue)
  return Number.isFinite(mileage) ? mileage : null
}

export const ArbitrationForm: React.FC<IProps> = ({
  vehicleInstanceID,
  buyerVehiclePurchaseID,
  vin,
  arbitrationState: _arbitrationState,
  isUpdateAttachment,
  metadata: metadataProp,
  onClose,
  onSuccess
}) => {
  const { setToastData, setShowToast } = useContext(ToastContext)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [formValues, setFormValues] = useState<IArbitrationFormValues | null>(null)
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const isAddAttachmentsMode = isUpdateAttachment

  const { data: fetchedMetadata, loading: metadataLoading } = useFetch<IArbitrationMetadata>(
    () => (metadataProp == null ? getArbitrationMetadata() : Promise.resolve(null)),
    [],
    []
  )

  const metadata = metadataProp ?? fetchedMetadata

  const { data: detail, loading: detailLoading } = useFetch<IArbitrationDetail>(
    () => getArbitrationInfo(vehicleInstanceID),
    [],
    [vehicleInstanceID]
  )

  const isLoading = (metadataProp == null && metadataLoading) || detailLoading

  const existingDocuments = detail?.existingDocuments ?? []
  const configuredMaxDocuments = metadata?.maxDocumentCount ?? ARBITRATION_MAX_DOCUMENTS
  const physicalDamageMinDocuments = metadata?.physicalDamageMinDocuments ?? ARBITRATION_MIN_PHYSICAL_DAMAGE_DOCUMENTS
  const nonPhysicalDamageMinDocuments = metadata?.nonPhysicalDamageMinDocuments ?? 0
  // Like BMW: effective max for new submission = full max; for add-attachments = remaining slots
  const effectiveMaxDocuments = isAddAttachmentsMode
    ? Math.max(0, configuredMaxDocuments - existingDocuments.length)
    : configuredMaxDocuments
  const attachmentSizeLimitMb = metadata?.fileSizeLimit ?? 10
  const acceptedAttachmentFormats = getAcceptedFileExtensions(metadata?.attachmentFormats)
  const attachmentAcceptValue = acceptedAttachmentFormats.map((format) => `.${format}`).join(',')

  const initialValues = useMemo((): IArbitrationFormValues => {
    const emailContactMethodID = metadata?.contactMethods.find((m) => m.description.toLowerCase() === 'email')?.id ?? 0
    if (!detail) return { ...initialArbitrationValues, contactMethodID: emailContactMethodID }
    return {
      contactName: detail.contactName || '',
      contactEmail: detail.contactEmail || '',
      contactPhone: detail.contactPhone || '',
      contactMethodID: detail.contactMethodID || emailContactMethodID,
      issueTypeID: detail.issueTypeID || 0,
      resolutionTypeID: detail.resolutionTypeID || 2,
      currentMileage: detail.currentMileage ? String(detail.currentMileage) : '',
      reason: detail.reason || '',
      documents: []
    }
  }, [detail, metadata])

  const doAddAttachments = useCallback(
    async (values: IArbitrationFormValues) => {
      if (!detail?.vehicleArbitrationID) return
      setIsSubmitting(true)
      setShowToast(false)
      try {
        const result = await addArbitrationDocuments({
          vehicleInstanceID,
          arbitrationID: detail.vehicleArbitrationID,
          salesforceCaseID: detail.salesforceCaseID || '',
          vin,
          documents: values.documents
        })
        if (!result.success) {
          setToastData(true, result.message || 'Failed to add attachments. Please try again.', 'error')
        } else {
          setToastData(true, 'Attachments added successfully.', 'infor')
          onSuccess(result, false)
        }
      } catch {
        setToastData(true, 'An unexpected error occurred. Please try again.', 'error')
      } finally {
        setIsSubmitting(false)
        onClose()
      }
    },
    [detail, vehicleInstanceID, vin, onSuccess, onClose, setShowToast, setToastData]
  )

  const handleConfirmSubmit = useCallback(async () => {
    if (!formValues || !detail) return
    setShowConfirmModal(false)
    setIsSubmitting(true)
    setShowToast(false)
    let isSubmitted = false
    try {
      const result = await submitArbitration({
        vehicleInstanceID,
        buyerVehiclePurchaseID,
        vin,
        contactName: formValues.contactName,
        contactEmail: formValues.contactEmail,
        contactPhone: formValues.contactPhone,
        contactMethodID: Number(formValues.contactMethodID),
        issueTypeID: Number(formValues.issueTypeID),
        resolutionTypeID: Number(formValues.resolutionTypeID),
        currentMileage: Number(formValues.currentMileage),
        reason: formValues.reason,
        documents: formValues.documents
      })
      if (!result.success) {
        setToastData(true, result.message || 'Arbitration submission failed. Please try again.', 'error')
      } else if (result.arbitrationState === ArbitrationState.Submitted) {
        setToastData(true, 'Submit arbitration successfully.', 'infor')
      } else {
        setToastData(true, 'An unexpected error occurred. Please try again.', 'error')
      }
      if (result.success && result.arbitrationState === ArbitrationState.Submitted) {
        isSubmitted = true
        onSuccess(result, true)
      }
    } catch {
      setToastData(true, 'An unexpected error occurred. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
      if (!isSubmitted) onClose()
    }
  }, [
    formValues,
    detail,
    vehicleInstanceID,
    buyerVehiclePurchaseID,
    vin,
    onSuccess,
    onClose,
    setShowToast,
    setToastData
  ])

  const handleFormSubmit = (values: IArbitrationFormValues) => {
    setFormValues(values)
    if (isAddAttachmentsMode) {
      doAddAttachments(values)
      return
    }
    setShowConfirmModal(true)
  }

  const handleCancelConfirm = useCallback(() => setShowConfirmModal(false), [])

  const confirmWarningMessage =
    Number(formValues?.issueTypeID) === ArbitrationIssueType.PhysicalDamage
      ? PHYSICAL_DAMAGE_SUBMIT_WARNING
      : NON_PHYSICAL_DAMAGE_DOCUMENT_HINT

  const renderFormContent = (formikProps: FormikProps<IArbitrationFormValues>) => {
    const processSelectedFiles = async (files: File[]) => {
      if (files.length === 0 || effectiveMaxDocuments === 0) return

      const maxSizeBytes = attachmentSizeLimitMb * 1024 * 1024

      if (formikProps.values.documents.length + files.length > effectiveMaxDocuments) {
        formikProps.setFieldTouched('documents', true, false)
        formikProps.setFieldError('documents', getMaximumAttachmentsMessage(effectiveMaxDocuments))
        return
      }

      if (files.some((file) => file.size > maxSizeBytes)) {
        formikProps.setFieldTouched('documents', true, false)
        formikProps.setFieldError('documents', FILE_SIZE_EXCEEDED_MESSAGE)
        return
      }

      if (files.some((file) => !acceptedAttachmentFormats.includes(file.name.split('.').pop()?.toLowerCase() || ''))) {
        formikProps.setFieldTouched('documents', true, false)
        formikProps.setFieldError('documents', UNSUPPORTED_FILE_TYPE_MESSAGE)
        return
      }

      const convertedFiles: IArbitrationDocumentSubmit[] = await Promise.all(
        files.map(async (file) => ({
          fileName: file.name,
          fileContent: getBase64Content(await toBase64(file)),
          mimeType: file.type
        }))
      )

      formikProps.setFieldValue('documents', [...formikProps.values.documents, ...convertedFiles])
      formikProps.setFieldError('documents', undefined)
      formikProps.setFieldTouched('documents', true, false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      await processSelectedFiles(Array.from(event.target.files || []))
    }

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      if (effectiveMaxDocuments === 0) return
      setIsDragActive(false)
      await processSelectedFiles(Array.from(event.dataTransfer.files || []))
    }

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      if (effectiveMaxDocuments > 0) setIsDragActive(true)
    }

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setIsDragActive(false)
    }

    const removeDocument = (index: number) => {
      formikProps.setFieldValue(
        'documents',
        formikProps.values.documents.filter((_, i) => i !== index)
      )
    }

    const selectedContactMethodDescription =
      metadata?.contactMethods.find((m) => m.id === Number(formikProps.values.contactMethodID))?.description || 'N/A'
    const parsedCurrentMileage = parseMileageInput(formikProps.values.currentMileage)
    const exceedsMileage =
      !isAddAttachmentsMode &&
      !!metadata &&
      !!detail &&
      parsedCurrentMileage !== null &&
      parsedCurrentMileage > detail.saleMileage + metadata.maxMileageExceed
    const isMileageBelowSale =
      !isAddAttachmentsMode && !!detail && parsedCurrentMileage !== null && parsedCurrentMileage < detail.saleMileage
    const selectedIssueType = Number(formikProps.values.issueTypeID)
    const issueTypeHint =
      selectedIssueType === ArbitrationIssueType.PhysicalDamage
        ? PHYSICAL_DAMAGE_ISSUE_HINT
        : selectedIssueType === ArbitrationIssueType.NonPhysicalDamage
        ? NON_PHYSICAL_DAMAGE_DOCUMENT_HINT
        : null
    return (
      <StyledArbitrationFormBody onSubmit={formikProps.handleSubmit} noValidate>
        {detail && <SaleInfoSection detail={detail} />}

        <ContactInfoSection
          metadata={metadata}
          formik={formikProps}
          isAddAttachmentsMode={isAddAttachmentsMode}
          selectedContactMethodDescription={selectedContactMethodDescription}
        />

        {metadata && detail && (
          <ArbitrationRequestSection
            metadata={metadata}
            formik={formikProps}
            isAddAttachmentsMode={isAddAttachmentsMode}
            existingDocuments={existingDocuments}
            attachmentAcceptValue={attachmentAcceptValue}
            isDragActive={isDragActive}
            selectedIssueType={selectedIssueType}
            issueTypeHint={issueTypeHint}
            isMileageBelowSale={isMileageBelowSale}
            exceedsMileage={exceedsMileage}
            mileageBelowWarning={MILEAGE_BELOW_WARNING}
            mileageExceedWarning={MILEAGE_EXCEED_WARNING}
            reasonMaxLength={ARBITRATION_REASON_MAX_LENGTH}
            emptyUploaderText={EMPTY_UPLOADER_TEXT}
            fileInputRef={fileInputRef}
            onFileChange={handleFileChange}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onRemoveDocument={removeDocument}
          />
        )}

        <Modal.Footer>
          <div className="btn-group">
            <Button variant="outline-secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : isAddAttachmentsMode ? 'Add Attachments' : 'Complete'}
            </Button>
          </div>
        </Modal.Footer>
      </StyledArbitrationFormBody>
    )
  }

  return (
    <StyledArbitrationModal show onHide={onClose} size="lg" centered backdrop="static" keyboard={false}>
      {isSubmitting && (
        <StyledFormSubmitOverlay>
          <Spinner animation="border" variant="primary" />
        </StyledFormSubmitOverlay>
      )}

      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>{isAddAttachmentsMode ? 'Add Attachments' : 'Arbitration Form'}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <StyledArbitrationFormLoading>
            <Spinner animation="border" variant="primary" />
          </StyledArbitrationFormLoading>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={isAddAttachmentsMode ? undefined : arbitrationFormSchema}
            validate={(values) => {
              const errors: Partial<Record<keyof IArbitrationFormValues, string>> = {}

              if (values.documents.length > effectiveMaxDocuments) {
                errors.documents = getMaximumAttachmentsMessage(effectiveMaxDocuments)
              }

              if (isAddAttachmentsMode && values.documents.length === 0) {
                errors.documents = ATTACHMENTS_REQUIRED_MESSAGE
              }

              if (
                !isAddAttachmentsMode &&
                Number(values.issueTypeID) === ArbitrationIssueType.PhysicalDamage &&
                values.documents.length < physicalDamageMinDocuments
              ) {
                errors.documents = getMinimumAttachmentsMessage(physicalDamageMinDocuments)
              }

              if (
                !isAddAttachmentsMode &&
                Number(values.issueTypeID) === ArbitrationIssueType.NonPhysicalDamage &&
                values.documents.length < nonPhysicalDamageMinDocuments
              ) {
                errors.documents = getMinimumAttachmentsMessage(nonPhysicalDamageMinDocuments)
              }

              return errors
            }}
            onSubmit={handleFormSubmit}
            enableReinitialize
          >
            {renderFormContent}
          </Formik>
        )}
      </Modal.Body>

      <ConfirmSubmitModal
        show={showConfirmModal}
        warningMessage={confirmWarningMessage}
        onHide={handleCancelConfirm}
        onSubmit={handleConfirmSubmit}
      />
    </StyledArbitrationModal>
  )
}
